'use client';

import React, { ReactNode, Suspense, lazy, useEffect, useState, useMemo, useRef } from 'react';
import { VibeshipContext, type VibeshipConfig } from '../contexts/VibeshipContext';
import { ErrorBoundary } from './ErrorBoundary';
import { LoadingSpinner } from './LoadingSpinner';
import { VibeshipDevTools } from './VibeshipDevTools';
import { logger } from '../utils/logger';

// Browser-safe default config
const browserDefaultConfig = {
  scanPaths: ['./docs', './README.md'],
  include: ['**/*.md', '**/*.mdx'],
  exclude: ['**/node_modules/**', '**/.next/**', '**/.git/**', '**/dist/**', '**/build/**'],
  features: {
    ai: true,
    tasks: true,
    fileEditing: true,
    commandPalette: true,
    devtools: true,
  },
  ui: {
    theme: 'system' as const,
    hotkey: 'cmd+k',
    position: 'fixed' as const,
    showInProduction: false,
  },
  api: {
    basePath: '/api/vibeship',
    timeout: 30000,
  },
  cache: {
    enabled: true,
    ttl: 300000,
    maxSize: 1000,
  },
  experimental: {}
};

// Simple merge function for browser use
function simpleMerge(target: any, ...sources: any[]): any {
  if (!sources.length) return target;
  const source = sources.shift();
  
  if (target && source && typeof target === 'object' && typeof source === 'object') {
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (!target[key]) target[key] = {};
        simpleMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  
  return simpleMerge(target, ...sources);
}


// Type definitions for dependencies
export interface VibeshipProviderDependencies {
  // Task Panel dependencies
  getProjectTasks?: (params: {
    scanDirectory: string;
    maxDepth: string | number;
    filePatterns: string;
    excludePatterns: string;
  }) => Promise<any>;
  sseEndpoint?: string;
  getSSEClient?: (endpoint: string) => any;
  
  // AI Chat dependencies
  onLoadApiKey?: (provider: string) => Promise<string>;
  chatApiEndpoint?: string;
  
  // Settings Panel dependencies
  AISettingsPanel?: React.ComponentType<any>;
  HotkeyInput?: React.ComponentType<any>;
  
  // File Editor dependencies
  loadFileContent?: (filePath: string) => Promise<string>;
  saveFileContent?: (filePath: string, content: string) => Promise<void>;
}

// Note: TaskPanelOverlay is now loaded inside VibeshipDevTools component

/**
 * Props for the VibeshipProvider component
 * @public
 */
export interface VibeshipProviderProps extends VibeshipProviderDependencies {
  /** React children to render within the provider */
  children: ReactNode;
  /** Partial configuration to override defaults */
  config?: Partial<VibeshipConfig>;
  /** Disable the provider and all its features */
  disabled?: boolean;
  /** Custom error fallback UI */
  errorFallback?: (error: Error, errorInfo: React.ErrorInfo, reset: () => void) => ReactNode;
  /** Custom loading fallback UI */
  loadingFallback?: ReactNode;
  /** Positioning strategy for UI components */
  position?: 'fixed' | 'absolute' | 'relative';
  /** Container element for portal rendering */
  container?: HTMLElement | null;
}

interface ProviderState {
  isReady: boolean;
  config: VibeshipConfig;
  error: Error | null;
}

/**
 * Main provider component for Vibeship DevTools
 * Provides configuration, context, and lazy-loaded UI components
 * 
 * @param props - VibecodeProviderProps
 * @returns React element
 * 
 * @example
 * ```tsx
 * <VibeshipProvider config={{ features: { ai: true } }}>
 *   <App />
 * </VibeshipProvider>
 * ```
 * 
 * @public
 */
export function VibeshipProvider({
  children,
  config: userConfig = {},
  disabled = false,
  errorFallback,
  loadingFallback = <LoadingSpinner />,
  position = 'fixed',
  container,
  // Task Panel dependencies
  getProjectTasks,
  sseEndpoint = '/api/vibeship/tasks/stream',
  getSSEClient,
  // AI Chat dependencies
  onLoadApiKey,
  chatApiEndpoint = '/api/vibeship/ai/chat',
  // Settings Panel dependencies
  AISettingsPanel,
  HotkeyInput,
  // File Editor dependencies
  loadFileContent,
  saveFileContent,
}: VibeshipProviderProps) {
  // Initialize with merged config to avoid re-initialization
  const [state, setState] = useState<ProviderState>(() => {
    const initialConfig = simpleMerge({}, browserDefaultConfig, userConfig);
    
    // Debug logging
    logger.log('[VibeshipProvider] Initial config:', initialConfig);
    logger.log('[VibeshipProvider] Features enabled:', initialConfig.features);
    
    return {
      isReady: true,
      config: initialConfig,
      error: null,
    };
  });

  // Track if we've initialized to prevent re-runs
  const hasInitializedRef = useRef(false);

  // Only re-initialize if disabled state changes
  useEffect(() => {
    // If disabled, reset to not ready
    if (disabled && hasInitializedRef.current) {
      setState({
        isReady: false,
        config: browserDefaultConfig,
        error: null,
      });
      hasInitializedRef.current = false;
    } 
    // If not disabled and not initialized, set to ready
    else if (!disabled && !hasInitializedRef.current) {
      const finalConfig = simpleMerge({}, browserDefaultConfig, userConfig);
      setState({
        isReady: true,
        config: finalConfig,
        error: null,
      });
      hasInitializedRef.current = true;
    }
  }, [disabled]); // Only depend on disabled, not userConfig

  // Memoize context value
  const contextValue = useMemo(() => ({
    config: state.config,
    isReady: state.isReady,
  }), [state.config, state.isReady]);

  // Don't render anything if disabled
  if (disabled) {
    return (
      <VibeshipContext.Provider value={{ config: browserDefaultConfig, isReady: true }}>
        {children}
      </VibeshipContext.Provider>
    );
  }

  // Show loading state while initializing
  if (!state.isReady) {
    return (
      <VibeshipContext.Provider value={contextValue}>
        {children}
        {loadingFallback}
      </VibeshipContext.Provider>
    );
  }

  // Error handler for the main error boundary
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    logger.error('[VibeshipProvider] Caught error:', error, errorInfo);
    
    // You can add error reporting here
    // e.g., send to error tracking service
  };

  return (
    <ErrorBoundary
      name="VibeshipProvider"
      onError={handleError}
      fallback={errorFallback}
    >
      <VibeshipContext.Provider value={contextValue}>
        {children}
        
        {/* Only render DevTools UI in development or if explicitly enabled */}
        {(process.env.NODE_ENV === 'development' || state.config.ui?.showInProduction) && (
          <ErrorBoundary 
            name="VibeshipUI" 
            isolate
            fallback={(error, errorInfo, reset) => {
              logger.error('[VibeshipProvider] UI Error:', error);
              logger.error('[VibeshipProvider] Error Info:', errorInfo);
              return (
                <div style={{ 
                  position: 'fixed', 
                  bottom: '20px', 
                  right: '20px', 
                  background: 'red', 
                  color: 'white', 
                  padding: '10px',
                  borderRadius: '4px',
                  zIndex: 9999
                }}>
                  VibeshipUI failed to load: {error?.message}
                  <button onClick={reset} style={{ marginLeft: '10px' }}>Retry</button>
                </div>
              );
            }}
          >
            <Suspense fallback={loadingFallback}>
              <VibeshipDevTools />
            </Suspense>
          </ErrorBoundary>
        )}
      </VibeshipContext.Provider>
    </ErrorBoundary>
  );
}

// Note: VibeshipDevTools component is now imported from './VibeshipDevTools'